/**
 * The deck, seen all at once.
 *
 * Summoned over the editor and dismissed, never docked beside it — a split pane
 * would permanently halve the writing surface to serve a glance. Same reasoning
 * as the library rail on the editor pages.
 *
 * Three jobs, and the second is the one that earns the screen:
 *
 * 1. Show what the slides actually look like. Tiles are painted by `drawSlide`,
 *    the same function that writes the exported PNGs, so the preview cannot
 *    drift from the file — including its shape, which follows the aspect ratio
 *    chosen in Settings.
 * 2. Get you back to the source. Every tile is a button that selects its block
 *    in the editor, which makes this the fastest way to move around a long
 *    manuscript — the slides are the beats of the sermon.
 * 3. Start presenting, from the top or from any slide. The light table is where
 *    you already are when you decide the deck is ready.
 *
 * Tile *click* stays "jump to the source", because that is the gesture the
 * overview was built around and the one used a hundred times a sermon.
 * Presenting from a particular slide is a separate control on the tile, so
 * neither has to be discovered at the cost of the other.
 */

import { useCallback, useEffect, useMemo, useRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Play, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { drawSlideScaled } from "@/lib/slide-render";
import { parseSlides, type Slide } from "@/lib/slides";
import { useTheme } from "@/contexts/ThemeContext";
import type { SlideStyle } from "@/lib/slide-themes";
import { ExportMenu } from "@/components/editor/ExportMenu";

/**
 * Thumbnail resolution as a fraction of the full slide frame.
 *
 * 0.4 gives a 768px-wide bitmap for a tile that displays around 380px, so it
 * still looks sharp on a retina screen without holding a full-size canvas per
 * slide in memory.
 */
const THUMBNAIL_RATIO = 0.4;

function SlideTile({
  slide,
  total,
  style,
  onSelect,
  onPresent,
  buttonRef,
}: {
  slide: Slide;
  total: number;
  style: SlideStyle;
  onSelect: () => void;
  onPresent: () => void;
  buttonRef?: React.Ref<HTMLButtonElement>;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (canvasRef.current) {
      drawSlideScaled(canvasRef.current, slide, THUMBNAIL_RATIO, { total, ...style });
    }
  }, [slide, total, style]);

  return (
    <div className="group relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={onSelect}
        data-slide-tile
        className="block w-full text-left focus:outline-none"
      >
        <div
          style={{ background: style.theme.background }}
          className="relative overflow-hidden rounded-lg border border-border shadow-sm transition-all group-hover:-translate-y-0.5 group-hover:shadow-lg group-hover:border-primary/60 group-focus-within:ring-2 group-focus-within:ring-primary group-focus-within:ring-offset-2 group-focus-within:ring-offset-background">
          <canvas
            ref={canvasRef}
            className="block h-auto w-full"
            style={{ aspectRatio: `${style.aspect.width} / ${style.aspect.height}` }}
            aria-hidden="true"
          />
        </div>
        <div className="mt-2 flex items-baseline gap-2 px-0.5">
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {slide.index + 1}
          </span>
          <span className="truncate text-sm text-foreground transition-colors group-hover:text-primary">
            {slide.title}
          </span>
        </div>
      </button>

      {/* Its own button, not a second meaning for the tile: presenting from
          here and jumping to the source are both wanted, and one gesture
          cannot be both. Always in the DOM so it is reachable by keyboard;
          only visually quiet until the tile is hovered or focused. */}
      <button
        type="button"
        onClick={onPresent}
        title={`Present from slide ${slide.index + 1}`}
        className="absolute right-2 top-2 rounded-full bg-background/90 p-2 text-foreground opacity-0 shadow-sm backdrop-blur transition-opacity hover:bg-background focus:outline-none focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-primary group-hover:opacity-100"
      >
        <Play className="h-3.5 w-3.5" />
        <span className="sr-only">Present from slide {slide.index + 1}</span>
      </button>
    </div>
  );
}

interface SlideOverviewProps {
  /**
   * The markdown to show, captured when the overview opened, or null when it is
   * closed. Taken as a snapshot rather than read live because the editor page
   * deliberately does not re-render while someone is typing.
   */
  source: string | null;
  title: string;
  onClose: () => void;
  /** Select this slide's block back in the editor. */
  onJumpTo: (slide: Slide) => void;
  /** Start presenting, from this slide's position in the deck. */
  onPresent: (index: number) => void;
}

export function SlideOverview({
  source,
  title,
  onClose,
  onJumpTo,
  onPresent,
}: SlideOverviewProps) {
  const slides = useMemo(() => (source ? parseSlides(source).slides : []), [source]);
  const { slideStyle } = useTheme();
  const firstTileRef = useRef<HTMLButtonElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  /**
   * Land on the deck, not the close button.
   *
   * Radix focuses the first focusable child, which is the X in the corner — so
   * opening the overview put a focus ring on "leave". Keyboard users should
   * arrive on the first slide, one Return away from jumping to it.
   */
  const focusFirstTile = useCallback((event: Event) => {
    if (!firstTileRef.current) return;
    event.preventDefault();
    firstTileRef.current.focus();
  }, []);

  /**
   * Arrow keys walk the grid.
   *
   * Tab alone would work, but a deck is laid out in rows and columns and Tab
   * only knows about one long sequence — reaching slide 14 of 30 meant
   * fourteen presses. The column count is measured from where the tiles
   * actually are rather than assumed from the breakpoint, so it stays right at
   * any window width.
   */
  const onGridKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    const keys = ["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp", "Home", "End"];
    if (!keys.includes(event.key)) return;

    const tiles = Array.from(
      gridRef.current?.querySelectorAll<HTMLButtonElement>("[data-slide-tile]") ?? [],
    );
    const current = tiles.findIndex((tile) => tile === document.activeElement);
    if (current < 0) return;

    const firstTop = tiles[0]?.getBoundingClientRect().top ?? 0;
    const columns = Math.max(
      1,
      tiles.filter((tile) => Math.abs(tile.getBoundingClientRect().top - firstTop) < 1).length,
    );

    const target = {
      ArrowRight: current + 1,
      ArrowLeft: current - 1,
      ArrowDown: current + columns,
      ArrowUp: current - columns,
      Home: 0,
      End: tiles.length - 1,
    }[event.key];

    if (target === undefined || target < 0 || target >= tiles.length) return;
    event.preventDefault();
    tiles[target].focus();
  }, []);

  return (
    <DialogPrimitive.Root
      open={source !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-background/85 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          onOpenAutoFocus={focusFirstTile}
          className="fixed inset-0 z-50 flex flex-col outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        >
          <header className="flex shrink-0 items-center gap-3 px-6 pb-4 pt-6">
            <div className="flex min-w-0 items-baseline gap-3">
              <DialogPrimitive.Title className="truncate text-lg font-medium text-foreground">
                {title}
              </DialogPrimitive.Title>
              <span className="shrink-0 text-sm text-muted-foreground">
                {slides.length} {slides.length === 1 ? "slide" : "slides"} ·{" "}
                {slideStyle.aspect.id}
              </span>
            </div>
            <DialogPrimitive.Description className="sr-only">
              Every slide in this document. Choose one to jump to it in the editor, or
              present the deck.
            </DialogPrimitive.Description>

            <span className="ml-auto hidden shrink-0 text-xs text-muted-foreground lg:inline">
              Arrow keys to move · Return to edit · Esc to close
            </span>

            {slides.length > 0 && (
              <div className="flex shrink-0 items-center gap-1">
                {/* The deck's own export menu. Reaching it used to mean closing
                    the overview and finding the editor's menu again, which is
                    the wrong way round: this is the screen where you decide the
                    deck is finished. */}
                <ExportMenu title={title} content={source ?? ""} />
                <Button size="sm" className="h-8 gap-1.5" onClick={() => onPresent(0)}>
                  <Play className="h-3.5 w-3.5" />
                  Present
                </Button>
              </div>
            )}

            <DialogPrimitive.Close className="ml-1 shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </header>

          <div className="flex-1 overflow-y-auto px-6 pb-10">
            {slides.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                This document has no slides yet. Type <code>/slide</code> in the editor to
                add one.
              </p>
            ) : (
              <div
                ref={gridRef}
                onKeyDown={onGridKeyDown}
                className="mx-auto grid max-w-6xl grid-cols-1 gap-x-5 gap-y-6 sm:grid-cols-2 lg:grid-cols-3"
              >
                {slides.map((slide) => (
                  <SlideTile
                    key={slide.start}
                    slide={slide}
                    total={slides.length}
                    style={slideStyle}
                    onSelect={() => onJumpTo(slide)}
                    onPresent={() => onPresent(slide.index)}
                    buttonRef={slide.index === 0 ? firstTileRef : undefined}
                  />
                ))}
              </div>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
